import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Select,
  Button,
  HStack,
  FormControl,
  FormLabel,
  Stack,
  Input,
  Collapse,
  useDisclosure,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import api from '../../services/api';

const TasksFilter = ({ filters, onFilterChange, onClearFilters }) => {
  const { isOpen, onToggle } = useDisclosure();
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  
  // Fetch users for the assignee filter
  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates after unmount
    
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const response = await api.get('/api/users');
        // Only update state if component is still mounted
        if (isMounted && response.data && response.data.users) {
          setUsers(response.data.users);
        }
      } catch (error) {
        if (isMounted) {
          console.error('[TasksFilter] Error fetching users:', error);
        }
      } finally {
        if (isMounted) {
          setIsLoadingUsers(false);
        }
      }
    };
    
    fetchUsers();
    
    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, []);
  
  return (
    <Box borderWidth="1px" borderRadius="md" p={4} mb={4}>
      <Flex justify="space-between" align="center" onClick={onToggle} cursor="pointer">
        <HStack>
          <Button
            size="sm"
            variant="ghost"
            rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
          >
            Filters & Sorting
          </Button>
        </HStack>
        
        <Button
          size="sm"
          variant="outline"
          onClick={(e) => {
            e.stopPropagation();
            onClearFilters();
          }}
        >
          Clear Filters
        </Button>
      </Flex>
      
      <Collapse in={isOpen} animateOpacity>
        <Stack direction={["column", "row"]} spacing={4} mt={4} wrap="wrap">
          <FormControl minW="150px" maxW="200px">
            <FormLabel fontSize="sm">Status</FormLabel>
            <Select
              size="sm"
              value={filters.status}
              onChange={(e) => onFilterChange({ status: e.target.value })}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending (Default)</option>
              <option value="completed">Completed</option>
              <option value="in_progress">In Progress</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </FormControl>
          
          <FormControl minW="150px" maxW="200px">
            <FormLabel fontSize="sm">Priority</FormLabel>
            <Select
              size="sm"
              value={filters.priority}
              onChange={(e) => onFilterChange({ priority: e.target.value })}
            >
              <option value="">All Priorities</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </Select>
          </FormControl>
          
          <FormControl minW="150px" maxW="200px">
            <FormLabel fontSize="sm">Task Assigned</FormLabel>
            <Select
              size="sm"
              value={filters.assignedTo}
              onChange={(e) => onFilterChange({ assignedTo: e.target.value })}
              isDisabled={isLoadingUsers}
            >
              <option value="">All Users</option>
              {users.map(user => (
                <option key={user.uid} value={user.uid}>
                  {user.displayName || user.email}
                </option>
              ))}
            </Select>
          </FormControl>
          
          <FormControl minW="150px" maxW="200px">
            <FormLabel fontSize="sm">Sort By</FormLabel>
            <Select
              size="sm"
              value={filters.sortField}
              onChange={(e) => onFilterChange({ sortField: e.target.value })}
            >
              <option value="dueDate">Due Date</option>
              <option value="createdAt">Created Date</option>
              <option value="priority">Priority</option>
              <option value="title">Title</option>
            </Select>
          </FormControl>
          
          <FormControl minW="150px" maxW="200px">
            <FormLabel fontSize="sm">Sort Order</FormLabel>
            <Select
              size="sm"
              value={filters.sortOrder}
              onChange={(e) => onFilterChange({ sortOrder: e.target.value })}
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </Select>
          </FormControl>
          
          <FormControl minW="150px" maxW="200px">
            <FormLabel fontSize="sm">Due Date From</FormLabel>
            <Input
              size="sm"
              type="date"
              value={filters.dueDateFrom || ''}
              onChange={(e) => onFilterChange({ dueDateFrom: e.target.value })}
            />
          </FormControl>
          
          <FormControl minW="150px" maxW="200px">
            <FormLabel fontSize="sm">Due Date To</FormLabel>
            <Input
              size="sm"
              type="date"
              value={filters.dueDateTo || ''}
              onChange={(e) => onFilterChange({ dueDateTo: e.target.value })}
            />
          </FormControl>
        </Stack>
        
        <Flex justify="flex-end" mt={4}>
          <Button
            colorScheme="blue"
            size="sm"
            onClick={() => onFilterChange(filters)}
          >
            Apply Filters
          </Button>
        </Flex>
      </Collapse>
    </Box>
  );
};

export default TasksFilter;
